import React, { useState, useCallback } from 'react';
import { Book } from 'lucide-react';
import { cn } from '@/lib/utils';
import { 
  resolveCoverUrl, 
  generateSrcSet, 
  createAltText, 
  logCoverDebug,
  COVER_DIMENSIONS,
  type CoverSource, 
  type CoverSize 
} from '@/lib/coverUtils';
import { CoverImageSkeleton } from './CoverImageSkeleton';

interface CoverImageProps extends CoverSource {
  size?: CoverSize;
  className?: string;
  loading?: 'lazy' | 'eager';
  fetchPriority?: 'high' | 'low' | 'auto';
  onClick?: () => void;
  showPlaceholder?: boolean;
}

/**
 * Unified cover image component with robust fallback handling
 * Used across all book listings, grids, and detail views
 */
export function CoverImage({ 
  bookId,
  isbn13,
  isbn10,
  olid,
  title,
  author,
  coverUrl,
  coverCacheUrl,
  size = 'md',
  className,
  loading = 'lazy',
  fetchPriority = 'auto',
  onClick,
  showPlaceholder = true
}: CoverImageProps) {
  const [imageState, setImageState] = useState<'loading' | 'loaded' | 'error'>('loading');
  const [currentSrc, setCurrentSrc] = useState<string | null>(null);

  const source: CoverSource = { bookId, isbn13, isbn10, olid, title, author, coverUrl, coverCacheUrl };
  const { container } = COVER_DIMENSIONS[size];
  const altText = createAltText(source);

  // Resolve primary URL
  const primaryUrl = resolveCoverUrl(source, size);

  // Handle image load success
  const handleLoad = useCallback(() => {
    setImageState('loaded');
    logCoverDebug(source, currentSrc, size);
  }, [source, currentSrc, size]);

  // Handle image load error with fallback chain
  const handleError = useCallback(() => {
    logCoverDebug(source, currentSrc, size);
    
    // If we haven't tried the primary URL yet, try it
    if (!currentSrc && primaryUrl) {
      setCurrentSrc(primaryUrl);
      return;
    }

    // If current source is our Supabase covers proxy, try progressive fallbacks
    if (currentSrc && currentSrc.includes('/functions/v1/covers?')) {
      try {
        const url = new URL(currentSrc);
        const params = url.searchParams;

        // 1) If prefer=gb failed and we have OLID, try prefer=ol as last resort
        if (params.get('prefer') === 'gb' && params.has('olid')) {
          params.set('prefer', 'ol');
          params.set('v', 'ol');
          url.search = params.toString();
          setCurrentSrc(url.toString());
          return;
        }

        // 2) If no prefer set and we have ISBN, try Open Library
        if (!params.has('prefer') && (params.has('isbn13') || params.has('isbn10'))) {
          params.set('prefer', 'ol');
          params.set('v', 'ol');
          url.search = params.toString();
          setCurrentSrc(url.toString());
          return;
        }

        // 3) If title is available and fallback not yet enabled, try title/author fallback
        const hasTitle = Boolean(title && bookId);
        if (hasTitle && !params.has('fallback')) {
          // Remove identifier params so fallback branch can activate server-side
          params.delete('isbn13');
          params.delete('isbn10');
          params.delete('olid');
          params.delete('prefer');

          params.set('title', title!);
          if (author) params.set('author', author);
          params.set('fallback', 'true');
          params.set('v', 'fb');
          url.search = params.toString();
          setCurrentSrc(url.toString());
          return;
        }
      } catch (e) {
        // If URL parsing fails, continue to placeholder
      }
    }

    // All sources failed, show placeholder
    setImageState('error');
  }, [currentSrc, primaryUrl, source, size, title, author, bookId]);

  // Initialize with primary URL and reset when book identity changes
  React.useEffect(() => {
    // Reset state first
    setCurrentSrc(null);
    setImageState('loading');
    
    // Then initialize with new URL
    if (primaryUrl) {
      setCurrentSrc(primaryUrl);
    } else {
      setImageState('error');
    }
  }, [primaryUrl, bookId, isbn13, isbn10, olid, coverUrl, coverCacheUrl]);

  // Show skeleton while loading
  if (imageState === 'loading' && !currentSrc) {
    return <CoverImageSkeleton size={size} className={className} />;
  }

  // Show placeholder on error (if enabled)
  if (imageState === 'error' && showPlaceholder) {
    const firstLetter = title?.charAt(0)?.toUpperCase() || '?';
    
    return (
      <div 
        className={cn(
          'relative aspect-[2/3] overflow-hidden rounded-md shadow-sm',
          'bg-primary/80 flex flex-col items-center justify-center',
          'cursor-pointer transition-all duration-200 hover:bg-primary/90',
          container,
          className
        )}
        onClick={onClick}
        role={onClick ? 'button' : undefined}
        aria-label={onClick ? `Open ${altText}` : altText}
        tabIndex={onClick ? 0 : undefined}
        onKeyDown={onClick ? (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick();
          }
        } : undefined}
      >
        {/* Book icon */}
        <Book 
          className={cn(
            'text-primary-foreground/80 mb-1',
            size === 'xs' ? 'w-4 h-4' : size === 'sm' ? 'w-5 h-5' : 'w-6 h-6'
          )} 
        />
        
        {/* First letter of title */}
        {title && (
          <span 
            className={cn(
              'font-heading font-bold text-primary-foreground/90',
              size === 'xs' ? 'text-xs' : size === 'sm' ? 'text-sm' : 'text-base'
            )}
          >
            {firstLetter}
          </span>
        )}
      </div>
    );
  }

  // Don't render anything if no placeholder and error
  if (imageState === 'error' && !showPlaceholder) {
    return null;
  }

  return (
    <div 
      className={cn(
        'relative aspect-[2/3] overflow-hidden rounded-md shadow-sm',
        'cursor-pointer focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        container,
        className
      )}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      aria-label={onClick ? `Open ${altText}` : altText}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      } : undefined}
    >
      {currentSrc && (
        <img
          src={currentSrc}
          srcSet={generateSrcSet(currentSrc, size)}
          alt={altText}
          className="absolute inset-0 h-full w-full object-cover"
          loading={loading}
          fetchPriority={fetchPriority}
          decoding="async"
          onLoad={handleLoad}
          onError={handleError}
        />
      )}
      
      {/* Loading state overlay */}
      {imageState === 'loading' && currentSrc && (
        <div className="absolute inset-0 bg-muted/50 animate-pulse" />
      )}
    </div>
  );
}

// Memoize the component to prevent unnecessary re-renders
export default React.memo(CoverImage);